import { useEffect, useMemo, useState } from 'react';
import {
  dispatchApi,
  peopleApi,
  vehiclesApi,
  type AssignmentCrewMember,
  type AssignmentWarning,
  type Person,
  type Vehicle,
} from '@/api/client';
import { useAsync } from '@/hooks';
import { Modal } from './Modal';
import { Select } from './Select';
import { Button } from './Button';

interface AssignmentModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (assignment: { vehicleId: string; crew: AssignmentCrewMember[] }) => Promise<void>;
  loading?: boolean;
  submitError?: string;
  /** Which trip is being assigned — shown in the title and used to rank vehicles by requested type. */
  trip?: AssignmentTripContext | null;
}

export interface AssignmentTripContext {
  /** Human reference, e.g. trip number. */
  label?: string | null;
  clientName?: string | null;
  vehicleTypeId?: string | null;
  vehicleTypeName?: string | null;
}

const UNAVAILABLE_VEHICLE_STATUSES: Record<string, string> = {
  maintenance: 'in maintenance',
  unavailable: 'unavailable',
  inactive: 'inactive',
  retired: 'retired',
};

function personLabel(p: Person): string {
  return p.fullName ?? p.name ?? 'Unknown';
}

export function AssignmentModal({ open, onClose, onSubmit, loading, submitError, trip }: AssignmentModalProps) {
  const { data: vehicles } = useAsync(() => (open ? vehiclesApi.list() : Promise.resolve([])), [open]);
  const { data: crew } = useAsync(() => (open ? peopleApi.list({ isCrew: true }) : Promise.resolve([])), [open]);

  const [vehicleId, setVehicleId] = useState('');
  const [driverId, setDriverId] = useState('');
  const [helper1Id, setHelper1Id] = useState('');
  const [helper2Id, setHelper2Id] = useState('');
  const [warnings, setWarnings] = useState<AssignmentWarning[]>([]);
  const [validating, setValidating] = useState(false);

  useEffect(() => {
    if (!open) {
      setVehicleId('');
      setDriverId('');
      setHelper1Id('');
      setHelper2Id('');
      setWarnings([]);
    }
  }, [open]);

  const crewPayload: AssignmentCrewMember[] = useMemo(() => {
    const list: AssignmentCrewMember[] = [];
    if (driverId) list.push({ personId: driverId, role: 'driver' });
    if (helper1Id) list.push({ personId: helper1Id, role: 'helper_1' });
    if (helper2Id) list.push({ personId: helper2Id, role: 'helper_2' });
    return list;
  }, [driverId, helper1Id, helper2Id]);

  useEffect(() => {
    if (!vehicleId || !driverId) {
      setWarnings([]);
      return;
    }
    let cancelled = false;
    setValidating(true);
    dispatchApi
      .validateAssignment({ vehicleId, crew: crewPayload })
      .then((rows) => { if (!cancelled) setWarnings(rows); })
      .catch(() => { if (!cancelled) setWarnings([]); })
      .finally(() => { if (!cancelled) setValidating(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, driverId, helper1Id, helper2Id]);

  const blockingWarnings = warnings.filter((w) => w.blocks);
  const canSubmit = Boolean(vehicleId && driverId) && !loading && !validating && blockingWarnings.length === 0;
  const submitHint = !vehicleId || !driverId
    ? 'Choose a vehicle and a driver to continue.'
    : validating
      ? 'Checking compliance…'
      : blockingWarnings.length > 0
        ? 'Resolve the blocking issues above (or pick different crew/vehicle) to continue.'
        : '';

  const requestedTypeId = trip?.vehicleTypeId ?? null;
  const requestedTypeName =
    trip?.vehicleTypeName ??
    (requestedTypeId
      ? (vehicles ?? []).find((v) => v.vehicleTypeId === requestedTypeId)?.vehicleTypeName ?? null
      : null);

  const sortedVehicles = useMemo(() => {
    const list = [...(vehicles ?? [])];
    const rank = (v: Vehicle) => {
      const unavailable = Boolean(UNAVAILABLE_VEHICLE_STATUSES[v.status]);
      const matches = requestedTypeId ? v.vehicleTypeId === requestedTypeId : true;
      return (unavailable ? 2 : 0) + (matches ? 0 : 1);
    };
    return list.sort((a, b) => rank(a) - rank(b) || (a.plateNumber ?? '').localeCompare(b.plateNumber ?? ''));
  }, [vehicles, requestedTypeId]);

  const titleParts = [trip?.label, trip?.clientName, requestedTypeName].filter(Boolean);
  const title = titleParts.length > 0 ? `Assign ${titleParts.join(' · ')}` : 'Assign vehicle & crew';

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await onSubmit({ vehicleId, crew: crewPayload });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <>
          {submitHint && !loading && <span className="text-muted assign-submit-hint">{submitHint}</span>}
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="button" onClick={handleSubmit} loading={loading} disabled={!canSubmit}>
            Assign & approve
          </Button>
        </>
      }
    >
      <p className="text-muted assign-intro">
        A trip cannot be approved without an assigned vehicle and driver.
      </p>

      {submitError && <div className="form-error">{submitError}</div>}

      <Select
        label="Vehicle"
        value={vehicleId}
        onChange={(e) => setVehicleId(e.target.value)}
        required
        hint={requestedTypeName ? `Vehicles matching the requested ${requestedTypeName} are listed first.` : undefined}
      >
        <option value="">Select vehicle…</option>
        {sortedVehicles.map((v: Vehicle) => {
          const unavailableLabel = UNAVAILABLE_VEHICLE_STATUSES[v.status];
          const typeName = v.vehicleTypeName ?? v.vehicleTypeCode ?? '';
          const mismatch = requestedTypeId && v.vehicleTypeId !== requestedTypeId;
          return (
            <option key={v.id} value={v.id} disabled={Boolean(unavailableLabel)}>
              {v.plateNumber ?? v.plate}
              {typeName ? ` — ${typeName}` : ''}
              {mismatch ? ' (different type)' : ''}
              {v.status === 'on_trip' ? ' · on a trip' : ''}
              {unavailableLabel ? ` · ${unavailableLabel}` : ''}
            </option>
          );
        })}
      </Select>

      <Select label="Driver (required)" value={driverId} onChange={(e) => setDriverId(e.target.value)} required>
        <option value="">Select driver…</option>
        {(crew ?? []).map((p: Person) => (
          <option key={p.id} value={p.id}>{personLabel(p)}</option>
        ))}
      </Select>

      <Select label="Helper 1 (optional)" value={helper1Id} onChange={(e) => setHelper1Id(e.target.value)}>
        <option value="">None</option>
        {(crew ?? []).filter((p: Person) => p.id !== driverId && p.id !== helper2Id).map((p: Person) => (
          <option key={p.id} value={p.id}>{personLabel(p)}</option>
        ))}
      </Select>

      <Select label="Helper 2 (optional)" value={helper2Id} onChange={(e) => setHelper2Id(e.target.value)}>
        <option value="">None</option>
        {(crew ?? []).filter((p: Person) => p.id !== driverId && p.id !== helper1Id).map((p: Person) => (
          <option key={p.id} value={p.id}>{personLabel(p)}</option>
        ))}
      </Select>

      {validating && <p className="text-muted">Checking compliance…</p>}

      {warnings.length > 0 && (
        <ul className="warning-list">
          {warnings.map((w, i) => (
            <li key={i} className={w.blocks ? 'warning-blocking' : 'warning-soft'}>
              {w.blocks ? '⛔' : '⚠️'} {w.message}
            </li>
          ))}
        </ul>
      )}
      {blockingWarnings.length > 0 && (
        <p className="text-urgent" style={{ fontSize: '0.8125rem' }}>
          This assignment has compliance blocks and will be rejected until resolved.
        </p>
      )}

      <style>{`
        .assign-intro { margin-bottom: var(--space-md); }
        .assign-submit-hint { font-size: 0.8125rem; margin-right: auto; align-self: center; }
        .form-error {
          background: #FDE8E8;
          color: var(--color-urgent);
          padding: var(--space-sm) var(--space-md);
          border-radius: var(--radius-sm);
          margin-bottom: var(--space-md);
          font-size: 0.875rem;
        }
        .warning-list {
          list-style: none;
          padding: 0;
          margin: var(--space-md) 0;
          font-size: 0.8125rem;
          display: flex;
          flex-direction: column;
          gap: var(--space-xs);
        }
        .warning-blocking { color: var(--color-urgent); }
        .warning-soft { color: var(--color-warning); }
      `}</style>
    </Modal>
  );
}
